import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const MONTH_NAMES = ["Jan","Shk","Mar","Pri","Maj","Qer","Kor","Gus","Sht","Tet","Nën","Dhj"];

export default function Reports() {
  const { format, symbol } = useCurrency();
  const [revenue, setRevenue] = useState([]);
  const [byDoctor, setByDoctor] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/reports/revenue"),
      api.get("/reports/treatments-by-doctor"),
    ]).then(([r, d]) => {
      setRevenue(r.data.map((x) => {
        const [y, m] = x.month.split("-");
        return { ...x, label: `${MONTH_NAMES[Number(m) - 1]} ${y.slice(-2)}` };
      }));
      setByDoctor(d.data);
    }).catch(() => {});
  }, []);

  const totalYear = revenue.reduce((s, r) => s + r.revenue, 0);
  const maxMonth = revenue.reduce((m, r) => r.revenue > (m?.revenue || 0) ? r : m, null);

  return (
    <div className="space-y-10" data-testid="reports-page">
      <div>
        <div className="label-eyebrow">Analitika</div>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">Raportet</h1>
        <p className="mt-2 text-sm text-[#5C5C5C]">Pamje e të ardhurave dhe performancës klinike.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="surface-card p-6" data-testid="stat-year-revenue">
          <div className="label-eyebrow">Të ardhura · 12 muajt e fundit</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{format(totalYear)}</div>
        </div>
        <div className="surface-card p-6" data-testid="stat-best-month">
          <div className="label-eyebrow">Muaji më i mirë</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{maxMonth?.label || "—"}</div>
          <div className="text-sm text-[#5C5C5C] mt-1">{maxMonth ? format(maxMonth.revenue) : ""}</div>
        </div>
        <div className="surface-card p-6" data-testid="stat-doctor-count">
          <div className="label-eyebrow">Mjekë aktivë</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{byDoctor.length}</div>
        </div>
      </div>

      <div className="surface-card p-6 md:p-8" data-testid="revenue-chart">
        <div className="label-eyebrow">Të Ardhurat Mujore</div>
        <h2 className="mt-1 text-2xl font-semibold tracking-tight mb-6">12 Muajt e Fundit</h2>
        <div className="w-full h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={revenue} margin={{ left: -10, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E2DFD8" vertical={false} />
              <XAxis dataKey="label" stroke="#5C5C5C" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#5C5C5C" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}${symbol}`} />
              <Tooltip
                contentStyle={{ background: "#FFFFFF", border: "1px solid #E2DFD8", borderRadius: "8px" }}
                formatter={(v) => [format(v), "Të ardhura"]}
              />
              <Bar dataKey="revenue" fill="#2C4C3B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="surface-card overflow-hidden" data-testid="doctor-table">
        <div className="px-6 pt-6">
          <div className="label-eyebrow">Performanca</div>
          <h2 className="mt-1 text-2xl font-semibold tracking-tight mb-4">Trajtimet sipas Mjekut</h2>
        </div>
        {byDoctor.length === 0 ? (
          <div className="p-10 text-center text-sm text-[#5C5C5C]">Pa të dhëna ende.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FDFBF7] text-left">
              <tr>
                <th className="px-6 py-3 label-eyebrow">Mjeku</th>
                <th className="px-6 py-3 label-eyebrow text-right">Trajtime</th>
                <th className="px-6 py-3 label-eyebrow text-right">Të ardhura</th>
              </tr>
            </thead>
            <tbody>
              {byDoctor.map((d) => (
                <tr key={d.doctor} className="border-t border-[#E2DFD8]">
                  <td className="px-6 py-4 font-medium">{d.doctor}</td>
                  <td className="px-6 py-4 text-right">{d.count}</td>
                  <td className="px-6 py-4 text-right font-mono">{format(d.total_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
